// @flow
import React from 'react';
import {
  Box,
  Skeleton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

// Heights measured on the rendered form, so the skeleton and the space the page reserves for
// it match what replaces them. The panel classes supply the borders, the head margin, and the
// panel's bottom margin; the body rows below carry their own heights and 16px margins.
const SPACING = 16;
const PANEL_BORDERS = 2;
const PANEL_MARGIN_BOTTOM = 21;
const HEAD = 90;
const HEAD_FIELD_LABEL = 18;
const FIELD = 40;
const TERM_ROW = 62;
const RENT_ROW = 59;
const TRANSACTION_ROW = 79;
const ADD_TRANSACTION = 40;
const NOTE = 20;
const BUTTON = 40;

// The AsyncBox Skeleton slot is absolutely positioned and sized to the box, so the box must
// reserve the form's height itself while the form is not mounted. This is the height the box
// has once the form is in it, bottom margin included, so the content below does not move.
export const renewalOfferFormSkeletonHeight = ({
  withTransactions,
}: {
  withTransactions: boolean,
}): number =>
  PANEL_BORDERS +
  HEAD +
  SPACING +
  SPACING +
  TERM_ROW +
  SPACING +
  RENT_ROW +
  SPACING +
  (withTransactions ? TRANSACTION_ROW + ADD_TRANSACTION + SPACING : 0) +
  NOTE +
  SPACING +
  BUTTON +
  SPACING +
  PANEL_MARGIN_BOTTOM;

const LabeledField = ({ height }: { height: number }) => (
  <Box sx={{ flex: 1, height, display: 'flex', flexDirection: 'column' }}>
    <Skeleton variant="text" width="40%" height={height - FIELD} />
    <Skeleton variant="rectangular" height={FIELD} />
  </Box>
);

const LabeledRow = ({ height, fields }: { height: number, fields: number }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
    <Box sx={{ width: '25%', pb: 1 }}>
      <Skeleton variant="text" width="50%" />
    </Box>
    {Array.from({ length: fields }, (_, i) => (
      <LabeledField key={i} height={height} />
    ))}
  </Box>
);

const RenewalOfferFormSkeleton = ({
  withTransactions,
}: {
  withTransactions: boolean,
}) => (
  <Box
    className="panel unit-information panel-withheader"
    data-test="renewal-offer-form-skeleton"
  >
    <Box
      className="panel-head"
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Skeleton variant="text" width={160} />
      <Box sx={{ width: 220 }}>
        <Skeleton variant="text" width="50%" height={HEAD_FIELD_LABEL} />
        <Skeleton variant="rectangular" height={FIELD} />
      </Box>
    </Box>
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <LabeledRow height={TERM_ROW} fields={3} />
      </Box>
      <Box sx={{ mb: 2 }}>
        <LabeledRow height={RENT_ROW} fields={3} />
      </Box>
      {withTransactions && (
        <>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {Array.from({ length: 4 }, (_, i) => (
              <LabeledField key={i} height={TRANSACTION_ROW} />
            ))}
          </Box>
          <Box
            sx={{
              height: ADD_TRANSACTION,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Skeleton variant="text" width={140} />
          </Box>
        </>
      )}
      <Box sx={{ mt: 2, height: NOTE }}>
        <Skeleton variant="text" width="70%" />
      </Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Skeleton variant="rectangular" width={200} height={BUTTON} />
      </Box>
    </Box>
  </Box>
);

export default RenewalOfferFormSkeleton;
